/**
 * The route tree, defined in code.
 *
 * Code-based rather than file-based routing because file-based needs the
 * TanStack Router Vite plugin, and this build's dependency set is fixed by the
 * scaffold — a routing style is not worth a new package here. The tree is small
 * enough that having it in one file is an advantage anyway: `/_authed` is the
 * gate, and every route under it is visibly inside the gate.
 *
 * The gate is `beforeLoad`, which resolves the session before a component
 * mounts. That is presentation only. The API authenticates every request on its
 * own, and a person who defeats this guard reaches an app whose every call
 * answers 401 — which is the correct division: the client decides what to
 * render, the server decides what is true.
 */

import type { QueryClient } from '@tanstack/react-query';
import {
  createRootRouteWithContext,
  createRoute,
  createRouter,
  Outlet,
  redirect,
  useNavigate,
  useParams,
  useSearch,
} from '@tanstack/react-router';
import { ApiError } from './api/client.ts';
import { ensureSession } from './api/queries.ts';
import { NODE_TYPES, type SearchKind, type UserRole, type UserStatus } from './api/types.ts';
import { ErrorState } from './components/ui/feedback.tsx';
import { AppShell } from './layout/app-shell.tsx';
import { SchemaShell } from './layout/schema-shell.tsx';
import { AccessIndexPage } from './routes/access/index.tsx';
import { NodeAccessPage } from './routes/access/node.tsx';
import { PersonAccessPage } from './routes/access/person.tsx';
import {
  AcknowledgmentCyclePage,
  AcknowledgmentsPage,
  CompliancePage,
} from './routes/acknowledgments/index.ts';
import { ApprovalRequestPage, ApprovalsPage } from './routes/approvals/index.ts';
import { ClientListPage } from './routes/clients/list.tsx';
import {
  DocumentPage,
  DocumentVersionsPage,
  NewDocumentPage,
  ReviewDuePage,
} from './routes/documents/index.ts';
import { HelpIndexPage, HelpPage } from './routes/help/index.tsx';
import { HomePage } from './routes/home.tsx';
import { KnowledgeListPage } from './routes/knowledge/list.tsx';
import { NotificationsPage } from './routes/notifications.tsx';
import { OrganizationDetailPage } from './routes/organizations/detail.tsx';
import { OrganizationListPage } from './routes/organizations/list.tsx';
import { PersonDetailPage } from './routes/people/detail.tsx';
import { PeopleListPage } from './routes/people/list.tsx';
import { ProjectDetailPage } from './routes/projects/detail.tsx';
import { ProjectListPage } from './routes/projects/list.tsx';
import { SchemaIndexPage } from './routes/schema/index.tsx';
import { SearchPage, type SearchPageState } from './routes/search/index.tsx';
import { DeactivateUserPage } from './routes/settings/workspace/users/deactivate.tsx';
import { UserListPage } from './routes/settings/workspace/users/list.tsx';
import { SignInPage } from './routes/sign-in.tsx';
import { SpaceDetailPage } from './routes/spaces/detail.tsx';
import { SpaceListPage } from './routes/spaces/list.tsx';
import { NodeDetailPage } from './routes/spaces/node-detail.tsx';

export interface RouterContext {
  queryClient: QueryClient;
}

/**
 * The breadcrumb trail in `layout/shell-bar.tsx`, one route at a time.
 *
 * It lives on the route rather than in a path→label table beside it because a
 * table is a second copy of the tree: adding a route there and forgetting the
 * table produces a page with no trail, and nothing fails. A route with a
 * `crumb` contributes one; a route without one — every `$id` segment here —
 * contributes nothing, which is deliberate. A route knows its shape and not its
 * contents, so "Admin / People" is a claim it can make and "Admin / People /
 * Alice Admin" is not.
 */
declare module '@tanstack/react-router' {
  interface StaticDataRouteOption {
    crumb?: string;
  }
}

/**
 * Search schemas are declared with optional properties rather than left to
 * inference. Inference from a `validateSearch` that always returns every key
 * produces `{ error: string | undefined }` — a *required* key whose value may
 * be undefined — which makes every `<Link>` to the route have to spell out a
 * search object it does not care about. Optional is what these actually are.
 */
interface SignInSearch {
  error?: string;
  message?: string;
  redirect?: string;
}

interface PeopleSearch {
  /** `none` is not a status but the absence of an account (phase 2 §2.1). */
  status?: UserStatus | 'none';
  role?: UserRole;
  q?: string;
}

interface CapabilitySearch {
  capability?: string;
}

interface PersonAccessSearch {
  capability?: string;
  spaceId?: string;
}

/** Prefills for the creation form, both optional; see `documents/navigation.tsx`. */
interface NewDocumentSearch {
  spaceId?: string;
  parentNodeId?: string;
}

/**
 * Search state, spelled the way the API spells it (§9.2) rather than the way
 * the component holds it.
 *
 * `kind` is a comma-joined string and not a `SearchKind[]` because the router's
 * default stringifier JSON-encodes anything that is not a string — an array
 * would land in the address bar as `kind=%5B%22document%22%5D`, which is neither
 * what `GET /api/search` accepts nor what someone pasting a link would have
 * copied out of it. The one place the two vocabularies meet is
 * {@link SearchRoute}, which is the whole reason it exists.
 */
interface SearchPageSearch {
  q?: string;
  kind?: string;
  spaceId?: string;
  includeArchived?: boolean;
  offset?: number;
}

function optionalString(value: unknown): string | undefined {
  return typeof value === 'string' && value.length > 0 ? value : undefined;
}

/**
 * Search params arrive parsed, so a number may already be a number — but it may
 * equally be `"3"`, `"-1"`, or `"drop table"`, because the only thing between
 * this and the address bar is a person. Anything that is not a page offset is
 * page one.
 */
function pageOffset(value: unknown): number | undefined {
  const parsed = typeof value === 'number' ? value : Number.parseInt(String(value ?? ''), 10);
  return Number.isSafeInteger(parsed) && parsed > 0 ? parsed : undefined;
}

function isSearchKind(value: string): value is SearchKind {
  return (NODE_TYPES as readonly string[]).includes(value);
}

/**
 * The URL's `kind` as the page's `kinds`.
 *
 * An absent, empty, or unrecognisable filter is *every* kind rather than none.
 * That is the same reading `GET /api/search` gives an absent `kind`, and it is
 * the safe direction to fail in: a filter nobody asked for silently hides rows,
 * where a filter that widens is visible in the toggle group.
 */
function searchKinds(kind: string | undefined): readonly SearchKind[] {
  const parsed = (kind ?? '')
    .split(',')
    .map((part) => part.trim())
    .filter(isSearchKind);
  return parsed.length > 0 ? parsed : NODE_TYPES;
}

/**
 * The search page, with the URL as its state.
 *
 * `SearchPage` is uncontrolled when it is handed neither prop — it seeds itself
 * from `window.location.search` so a pasted deep link works with no route at
 * all. Now that the route exists, both props are passed and the address bar
 * becomes the single copy of the state: back, forward, reload, and "copy link"
 * all describe the same search.
 *
 * `replace: true` because a search box is not a sequence of destinations. Every
 * keystroke that changed the URL would otherwise be a history entry, and Back
 * would walk the term backwards one letter at a time.
 */
function SearchRoute() {
  const search = useSearch({ from: '/_authed/_employee/search' });
  const navigate = useNavigate();

  const state: SearchPageState = {
    q: search.q ?? '',
    kinds: searchKinds(search.kind),
    spaceId: search.spaceId,
    includeArchived: search.includeArchived ?? false,
    offset: search.offset ?? 0,
  };

  return (
    <SearchPage
      state={state}
      onStateChange={(next) => {
        void navigate({
          to: '/search',
          replace: true,
          // Undefined keys are dropped rather than serialised, so a default
          // never appears in the URL — `/search?q=onboarding` stays the link a
          // person would actually send.
          search: {
            q: next.q.length > 0 ? next.q : undefined,
            kind: next.kinds.length === NODE_TYPES.length ? undefined : next.kinds.join(','),
            spaceId: next.spaceId,
            includeArchived: next.includeArchived ? true : undefined,
            offset: next.offset > 0 ? next.offset : undefined,
          },
        });
      }}
    />
  );
}

const rootRoute = createRootRouteWithContext<RouterContext>()({
  component: () => <Outlet />,
  notFoundComponent: () => (
    <div className="mx-auto max-w-lg p-8">
      <h1 className="text-display-xs font-semibold text-primary">No such page</h1>
      <p className="mt-2 text-sm text-tertiary">
        Nothing is routed here. <a href="/">Back to Home</a>.
      </p>
    </div>
  ),
});

// --- the one public route (§11) -------------------------------------------

const signInRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/sign-in',
  /**
   * The API redirects failed sign-ins back here with its own reason and
   * message, so both are read rather than re-derived — the words a person sees
   * are the words the auth layer chose (`apps/api/src/auth/errors.ts`).
   */
  validateSearch: (search: Record<string, unknown>): SignInSearch => ({
    error: optionalString(search.error),
    message: optionalString(search.message),
    redirect: optionalString(search.redirect),
  }),
  component: SignInPage,
});

// --- everything else ------------------------------------------------------

const authedRoute = createRoute({
  getParentRoute: () => rootRoute,
  id: '_authed',
  beforeLoad: async ({ context, location }) => {
    try {
      await ensureSession(context.queryClient);
    } catch (error) {
      if (error instanceof ApiError && (error.status === 401 || error.status === 403)) {
        throw redirect({ to: '/sign-in', search: { redirect: location.href } });
      }
      throw error;
    }
  },
  errorComponent: ({ error }) => (
    <div className="p-2">
      <ErrorState error={error} />
    </div>
  ),
});

/**
 * §7.1 — "separate route trees with separate shells, not one tree rendering
 * conditionally".
 *
 * The gate above is shared because authentication is not what distinguishes the
 * surfaces. Everything below is not: the employee tree renders inside
 * {@link AppShell} and the Schema tree inside {@link SchemaShell}, so a screen
 * cannot drift between them by accident. A pathless route is what makes that a
 * layout rather than a URL segment — `/projects` is still `/projects`.
 */
const employeeRoute = createRoute({
  getParentRoute: () => authedRoute,
  id: '_employee',
  component: AppShell,
});

const homeRoute = createRoute({
  getParentRoute: () => employeeRoute,
  staticData: { crumb: 'Home' },
  path: '/',
  component: HomePage,
});

/**
 * People is a top-level destination, and Users is a setting.
 *
 * They were one screen behind `/admin`, which put a directory lookup behind the
 * capability that provisions logins. The split follows the rule the schema now
 * states: a person is a record in the CRM, a user is a person with an account.
 * Looking somebody up is ordinary work for every employee role; creating an
 * account for them is administration, and it lives with the other workspace
 * settings.
 *
 * `/admin/*` is kept as redirects rather than deleted. Those URLs have been
 * handed out, sit in browser histories, and are linked from the help pages;
 * a 404 would be a worse answer than the page they were looking for.
 */
const peopleRoute = createRoute({
  getParentRoute: () => employeeRoute,
  staticData: { crumb: 'People' },
  path: '/people',
});

const peopleIndexRoute = createRoute({
  getParentRoute: () => peopleRoute,
  path: '/',
  validateSearch: (search: Record<string, unknown>): PeopleSearch => ({
    status: optionalString(search.status) as UserStatus | 'none' | undefined,
    role: optionalString(search.role) as UserRole | undefined,
    q: optionalString(search.q),
  }),
  component: PeopleListPage,
});

const personRoute = createRoute({
  getParentRoute: () => peopleRoute,
  path: '/$personId',
});

const personIndexRoute = createRoute({
  getParentRoute: () => personRoute,
  path: '/',
  component: PersonDetailPage,
});

/**
 * Settings — workspace-wide administration. Users is the whole of it today,
 * which is why `/settings` and `/settings/workspace` redirect rather than
 * render: a section index listing one link is a page that exists to be clicked
 * through.
 */
const settingsRoute = createRoute({
  getParentRoute: () => employeeRoute,
  staticData: { crumb: 'Settings' },
  path: '/settings',
});

const settingsIndexRoute = createRoute({
  getParentRoute: () => settingsRoute,
  path: '/',
  beforeLoad: () => {
    throw redirect({ to: '/settings/workspace/users' });
  },
});

const workspaceRoute = createRoute({
  getParentRoute: () => settingsRoute,
  staticData: { crumb: 'Workspace' },
  path: '/workspace',
});

const workspaceIndexRoute = createRoute({
  getParentRoute: () => workspaceRoute,
  path: '/',
  beforeLoad: () => {
    throw redirect({ to: '/settings/workspace/users' });
  },
});

const usersRoute = createRoute({
  getParentRoute: () => workspaceRoute,
  staticData: { crumb: 'Users' },
  path: '/users',
});

const usersIndexRoute = createRoute({
  getParentRoute: () => usersRoute,
  path: '/',
  validateSearch: (search: Record<string, unknown>): PeopleSearch => ({
    status: optionalString(search.status) as UserStatus | 'none' | undefined,
    role: optionalString(search.role) as UserRole | undefined,
    q: optionalString(search.q),
  }),
  component: UserListPage,
});

/**
 * Deactivation is the account being taken away, so it sits under Users — and
 * it is keyed by person id like every other `/users` address, because that is
 * what the API is keyed by.
 */
const userDeactivateRoute = createRoute({
  getParentRoute: () => usersRoute,
  staticData: { crumb: 'Deactivate' },
  path: '/$personId/deactivate',
  component: DeactivateUserPage,
});

/** The addresses `/admin` used to answer on. */
const adminRoute = createRoute({
  getParentRoute: () => employeeRoute,
  path: '/admin',
});

const adminIndexRoute = createRoute({
  getParentRoute: () => adminRoute,
  path: '/',
  beforeLoad: () => {
    throw redirect({ to: '/people' });
  },
});

const adminPeopleRoute = createRoute({
  getParentRoute: () => adminRoute,
  path: '/people',
  beforeLoad: () => {
    throw redirect({ to: '/people' });
  },
});

const adminPersonRoute = createRoute({
  getParentRoute: () => adminRoute,
  path: '/people/$personId',
  beforeLoad: ({ params }) => {
    throw redirect({ to: '/people/$personId', params: { personId: params.personId } });
  },
});

const adminPersonDeactivateRoute = createRoute({
  getParentRoute: () => adminRoute,
  path: '/people/$personId/deactivate',
  beforeLoad: ({ params }) => {
    throw redirect({
      to: '/settings/workspace/users/$personId/deactivate',
      params: { personId: params.personId },
    });
  },
});

const organizationsRoute = createRoute({
  getParentRoute: () => employeeRoute,
  staticData: { crumb: 'Organizations' },
  path: '/organizations',
  component: OrganizationListPage,
});

const organizationRoute = createRoute({
  getParentRoute: () => employeeRoute,
  path: '/organizations/$organizationId',
  component: OrganizationDetailPage,
});

const projectsRoute = createRoute({
  getParentRoute: () => employeeRoute,
  staticData: { crumb: 'Projects' },
  path: '/projects',
  component: ProjectListPage,
});

const projectRoute = createRoute({
  getParentRoute: () => employeeRoute,
  path: '/projects/$projectId',
  component: ProjectDetailPage,
});

const clientsRoute = createRoute({
  getParentRoute: () => employeeRoute,
  staticData: { crumb: 'Clients' },
  path: '/clients',
  component: ClientListPage,
});

const knowledgeRoute = createRoute({
  getParentRoute: () => employeeRoute,
  staticData: { crumb: 'Knowledge' },
  path: '/knowledge',
  component: KnowledgeListPage,
});

/**
 * A space and a node keep their employee-facing URLs, and only the *raw list*
 * moves to Schema.
 *
 * The split is what §7.2 asks for rather than a compromise on it: Knowledge and
 * Projects are how a person arrives at a space, and a document's breadcrumb
 * points at the one it lives in. What belongs behind Schema is the flat tree of
 * every space with its depth counters and capability toggles — introspection
 * about the shape of the tree, not a way into the work.
 */
const spaceRoute = createRoute({
  getParentRoute: () => employeeRoute,
  path: '/spaces/$spaceId',
  component: SpaceDetailPage,
});

const nodeRoute = createRoute({
  getParentRoute: () => employeeRoute,
  path: '/nodes/$nodeId',
  component: NodeDetailPage,
});

// --- §7.3: the Schema surface ---------------------------------------------

const schemaRoute = createRoute({
  getParentRoute: () => authedRoute,
  staticData: { crumb: 'Schema' },
  path: '/schema',
  component: SchemaShell,
});

const schemaIndexRoute = createRoute({
  getParentRoute: () => schemaRoute,
  path: '/',
  component: SchemaIndexPage,
});

const schemaSpacesRoute = createRoute({
  getParentRoute: () => schemaRoute,
  staticData: { crumb: 'Spaces & nodes' },
  path: '/spaces',
  component: SpaceListPage,
});

const accessRoute = createRoute({
  getParentRoute: () => schemaRoute,
  staticData: { crumb: 'Effective access' },
  path: '/access',
  component: AccessIndexPage,
});

const accessNodeRoute = createRoute({
  getParentRoute: () => schemaRoute,
  staticData: { crumb: 'Effective access' },
  path: '/access/nodes/$nodeId',
  validateSearch: (search: Record<string, unknown>): CapabilitySearch => ({
    capability: optionalString(search.capability),
  }),
  component: NodeAccessPage,
});

const accessPersonRoute = createRoute({
  getParentRoute: () => schemaRoute,
  staticData: { crumb: 'Effective access' },
  path: '/access/people/$personId',
  validateSearch: (search: Record<string, unknown>): PersonAccessSearch => ({
    capability: optionalString(search.capability),
    spaceId: optionalString(search.spaceId),
  }),
  component: PersonAccessPage,
});

const notificationsRoute = createRoute({
  getParentRoute: () => employeeRoute,
  staticData: { crumb: 'Notifications' },
  path: '/notifications',
  component: NotificationsPage,
});

/**
 * Help — `docs/help/` rendered inside the shell, reachable from the bar.
 *
 * Inside `_employee` rather than beside it because help is read *while* using
 * the app, and a surface of its own would take the sidebar away from somebody
 * who opened it to work out where a button was.
 */
const helpRoute = createRoute({
  getParentRoute: () => employeeRoute,
  staticData: { crumb: 'Help' },
  path: '/help',
});

const helpIndexRoute = createRoute({
  getParentRoute: () => helpRoute,
  path: '/',
  component: HelpIndexPage,
});

const helpPageRoute = createRoute({
  getParentRoute: () => helpRoute,
  path: '/$page',
  component: function HelpPageRoute() {
    const { page } = useParams({ from: '/_authed/_employee/help/$page' });
    return <HelpPage slug={page} />;
  },
});

// --- phase 1a: documents and search ---------------------------------------

/**
 * There is no list of every document and there should not be one — §9 makes
 * search the way documents are found, and a space's tree is the way they are
 * browsed. So `/documents` redirects to `/search` rather than rendering
 * anything: a parent route with no component renders an empty `Outlet`, which
 * would be a blank page inside the shell rather than the not-found somebody
 * typing that URL deserves.
 *
 * `/new` and `/review` are declared as siblings of `/$nodeId` rather than
 * before it out of caution: the router ranks a static segment above a dynamic
 * one regardless of declaration order, so neither can be swallowed as a node id.
 */
const documentsRoute = createRoute({
  getParentRoute: () => employeeRoute,
  path: '/documents',
});

const documentsIndexRoute = createRoute({
  getParentRoute: () => documentsRoute,
  path: '/',
  beforeLoad: () => {
    throw redirect({ to: '/search' });
  },
});

const documentNewRoute = createRoute({
  getParentRoute: () => documentsRoute,
  staticData: { crumb: 'New document' },
  path: '/new',
  /**
   * Both are prefills and both are optional. The form asks for the space
   * regardless, and an id that names nothing the person can reach is refused by
   * `POST /api/documents` rather than here — a client that filtered the picker
   * would be a second copy of §7.1's table.
   */
  validateSearch: (search: Record<string, unknown>): NewDocumentSearch => ({
    spaceId: optionalString(search.spaceId),
    parentNodeId: optionalString(search.parentNodeId),
  }),
  component: NewDocumentPage,
});

/** §7.2 — the list behind "12 playbooks are past review". */
const documentReviewRoute = createRoute({
  getParentRoute: () => documentsRoute,
  staticData: { crumb: 'Past review' },
  path: '/review',
  component: ReviewDuePage,
});

const documentRoute = createRoute({
  getParentRoute: () => documentsRoute,
  path: '/$nodeId',
});

const documentIndexRoute = createRoute({
  getParentRoute: () => documentRoute,
  path: '/',
  component: DocumentPage,
});

const documentVersionsRoute = createRoute({
  getParentRoute: () => documentRoute,
  staticData: { crumb: 'Versions' },
  path: '/versions',
  component: DocumentVersionsPage,
});

const searchRoute = createRoute({
  getParentRoute: () => employeeRoute,
  staticData: { crumb: 'Search' },
  path: '/search',
  validateSearch: (search: Record<string, unknown>): SearchPageSearch => ({
    q: optionalString(search.q),
    kind: optionalString(search.kind),
    spaceId: optionalString(search.spaceId),
    // The API accepts the literal `true` and `false` and coerces nothing else
    // (`apps/api/src/search/routes.ts`), so anything else here is `false` rather
    // than an error — this is a URL somebody may have edited by hand, and the
    // search still works with the archived rows left out.
    includeArchived: search.includeArchived === true || search.includeArchived === 'true',
    offset: pageOffset(search.offset),
  }),
  component: SearchRoute,
});

// --- phase 1b: approvals and acknowledgments -------------------------------

/**
 * §7.6's two screens and §7.7's three.
 *
 * Both `$requestId` pages take the id as a **prop** rather than reading
 * `useParams` themselves, so the three-line adapters below are where the route
 * name is spelled. That is not ceremony: `from` is checked against the
 * *registered* tree, so a page that read its own params could not compile until
 * its route existed — and the route cannot exist until the page does. One of the
 * two has to break the cycle, and a prop is the cheaper end.
 *
 * `/acknowledgments/compliance` is a static segment beside a dynamic sibling.
 * The router ranks static above dynamic regardless of declaration order, so it
 * cannot be swallowed as a request id — the arrangement `/documents/new` already
 * relies on.
 *
 * Neither parent renders anything of its own, so both get an index child rather
 * than a component: a parent route with no component renders a bare `Outlet`,
 * which is a blank page inside the shell where the list belongs.
 */
const approvalsRoute = createRoute({
  getParentRoute: () => employeeRoute,
  staticData: { crumb: 'Approvals' },
  path: '/approvals',
});

const approvalsIndexRoute = createRoute({
  getParentRoute: () => approvalsRoute,
  path: '/',
  component: ApprovalsPage,
});

const approvalRequestRoute = createRoute({
  getParentRoute: () => approvalsRoute,
  path: '/$requestId',
  component: function ApprovalRequestRoute() {
    const { requestId } = useParams({ from: '/_authed/_employee/approvals/$requestId' });
    return <ApprovalRequestPage requestId={requestId} />;
  },
});

const acknowledgmentsRoute = createRoute({
  getParentRoute: () => employeeRoute,
  staticData: { crumb: 'Acknowledgments' },
  path: '/acknowledgments',
});

const acknowledgmentsIndexRoute = createRoute({
  getParentRoute: () => acknowledgmentsRoute,
  path: '/',
  component: AcknowledgmentsPage,
});

/** §7.7 — "admins see a cross-document compliance view". Guarded by `audit.view` at the API. */
const complianceRoute = createRoute({
  getParentRoute: () => acknowledgmentsRoute,
  staticData: { crumb: 'Compliance' },
  path: '/compliance',
  component: CompliancePage,
});

const acknowledgmentCycleRoute = createRoute({
  getParentRoute: () => acknowledgmentsRoute,
  path: '/$requestId',
  component: function AcknowledgmentCycleRoute() {
    const { requestId } = useParams({ from: '/_authed/_employee/acknowledgments/$requestId' });
    return <AcknowledgmentCyclePage requestId={requestId} />;
  },
});

const routeTree = rootRoute.addChildren([
  signInRoute,
  authedRoute.addChildren([
    employeeRoute.addChildren([
      homeRoute,
      peopleRoute.addChildren([peopleIndexRoute, personRoute.addChildren([personIndexRoute])]),
      settingsRoute.addChildren([
        settingsIndexRoute,
        workspaceRoute.addChildren([
          workspaceIndexRoute,
          usersRoute.addChildren([usersIndexRoute, userDeactivateRoute]),
        ]),
      ]),
      adminRoute.addChildren([
        adminIndexRoute,
        adminPeopleRoute,
        adminPersonRoute,
        adminPersonDeactivateRoute,
      ]),
      projectsRoute,
      projectRoute,
      clientsRoute,
      organizationsRoute,
      organizationRoute,
      knowledgeRoute,
      spaceRoute,
      nodeRoute,
      notificationsRoute,
      helpRoute.addChildren([helpIndexRoute, helpPageRoute]),
      documentsRoute.addChildren([
        documentsIndexRoute,
        documentNewRoute,
        documentReviewRoute,
        documentRoute.addChildren([documentIndexRoute, documentVersionsRoute]),
      ]),
      searchRoute,
      approvalsRoute.addChildren([approvalsIndexRoute, approvalRequestRoute]),
      acknowledgmentsRoute.addChildren([
        acknowledgmentsIndexRoute,
        complianceRoute,
        acknowledgmentCycleRoute,
      ]),
    ]),
    schemaRoute.addChildren([
      schemaIndexRoute,
      schemaSpacesRoute,
      accessRoute,
      accessNodeRoute,
      accessPersonRoute,
    ]),
  ]),
]);

export function buildRouter(queryClient: QueryClient) {
  return createRouter({
    routeTree,
    context: { queryClient },
    defaultPreload: 'intent',
    // Query owns caching; the router should not keep a second copy with its own
    // staleness rules.
    defaultPreloadStaleTime: 0,
  });
}

declare module '@tanstack/react-router' {
  interface Register {
    router: ReturnType<typeof buildRouter>;
  }
}
